import { useContext, useMemo } from 'react'

// eslint-disable-next-line max-len
import useGetLibraryItems from 'Pods/ThreeDStudio/Components/Sidebar/Components/LibraryContent/Hooks/useGetLibraryItems'
import { VARIATION_TYPES } from 'Pods/ThreeDStudio/constants'
import useGetVariations from 'Pods/ThreeDStudio/Hooks/useGetVariations'
import { isRenderPreviewOf } from 'Pods/ThreeDStudio/Shared/Utils/renderRun'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import usePrefetchVariationModels from './usePrefetchVariationModels'
import useVariationActions from './useVariationActions'

function useVariationsTab() {
  const pageContext = useContext(ThreeDStudioPageContext)
  const isLoggedIn = pageContext?.userLoggedIn
  const openedModel = useThreeDStudioStore(state => state.ui?.openedModel)
  const isUploadedModelLoaded = useThreeDStudioStore(state => !!state.modelSettings?.inputThreeDUrl)
  const renderPreview = useThreeDStudioStore(state => state.ui?.renderPreview)
  const sessionId = openedModel?.generatorSessionId

  const { variations, uploadedAsset, originalRunId, isLoading } = useGetVariations(sessionId)
  const { items: libraryItems } = useGetLibraryItems(isLoggedIn, '')
  const {
    assignMain,
    deleteVariation,
    isAssigning,
    isDeleting,
  } = useVariationActions(variations)

  usePrefetchVariationModels(variations)

  const mainRunId = useMemo(() => {
    const session = libraryItems.find(item => String(item.id) === String(sessionId))
    const mainRun = session?.attributes?.mainRun
    return mainRun?.id
  }, [libraryItems, sessionId])

  const isEnhanced = useMemo(() =>
    variations.some(variation => VARIATION_TYPES[variation.providerModelId]?.isEnhancement),
    [variations],
  )

  // A render run is selected while its image is in the Render preview, independently of
  // the model the viewer shows.
  const previewedRunId = useMemo(
    () => variations.find(variation => isRenderPreviewOf(variation, renderPreview))?.id ?? null,
    [variations, renderPreview],
  )

  // The original is numberless, like the uploaded-model card, so numbering counts only
  // the variations. Keyed by id rather than derived from the index because the original
  // is not guaranteed to be the oldest run in the list.
  const positionById = useMemo(() => {
    let position = 0
    return new Map(variations.map((variation) => {
      const isOriginal = String(variation.id) === String(originalRunId)
      position += isOriginal ? 0 : 1
      return [String(variation.id), isOriginal ? null : position]
    }))
  }, [variations, originalRunId])

  return {
    variations,
    uploadedAsset,
    isLoading,
    isEnhanced,
    isUploadedModelLoaded,
    openedModelId: openedModel?.id,
    previewedRunId,
    mainRunId,
    positionById,
    isActionLoading: isAssigning || isDeleting,
    assignMain,
    deleteVariation,
  }
}

export default useVariationsTab
